import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ForceGraph2D from 'react-force-graph-2d';
import { useAuth } from '../../contexts/AuthContext';
import { MindmapService, MindmapWithData, MindmapNode, MindmapEdge } from '../../services/mindmapService';
import PageHeader from '../common/PageHeader';
import MindmapNodeModal from './MindmapNodeModal';
import './Mindmap.css';

interface MindmapCanvasProps {
  onToggleSidebar: () => void;
}

interface GraphNode {
  id: number;
  label: string;
  color?: string;
  size: number;
  fx?: number;
  fy?: number;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface GraphLink {
  source: number | GraphNode;
  target: number | GraphNode;
  id: number;
  label?: string;
}

const MindmapCanvas: React.FC<MindmapCanvasProps> = ({ onToggleSidebar }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const graphRef = useRef<any>();
  const [mindmap, setMindmap] = useState<MindmapWithData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [editingNode, setEditingNode] = useState<MindmapNode | null>(null);
  const [fromNodeId, setFromNodeId] = useState<number | null>(null);
  const [newNodePosition, setNewNodePosition] = useState<{ x: number; y: number } | null>(null);
  const lastClickRef = useRef<{ nodeId: number; timestamp: number } | null>(null);

  useEffect(() => {
    if (user && id) {
      loadMindmap();
    }
  }, [user, id]);

  const loadMindmap = async () => {
    if (!user || !id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await MindmapService.getMindmap(parseInt(id));
      setMindmap(data);
    } catch (err) {
      console.error('Error loading mindmap:', err);
      setError('Failed to load mindmap');
    } finally {
      setLoading(false);
    }
  };

  // Transform data into graph format
  const graphData = useMemo(() => {
    if (!mindmap) return { nodes: [], links: [] };

    const nodes: GraphNode[] = mindmap.nodes.map(node => ({
      id: node.id,
      label: node.label,
      color: node.color || '#ffffff',
      size: node.size || 20,
      x: node.x || undefined,
      y: node.y || undefined,
      fx: node.x !== null && node.x !== undefined ? node.x : undefined,
      fy: node.y !== null && node.y !== undefined ? node.y : undefined
    }));

    const links: GraphLink[] = mindmap.edges.map(edge => ({
      id: edge.id,
      source: edge.from_node_id,
      target: edge.to_node_id,
      label: edge.label || undefined
    }));

    return { nodes, links };
  }, [mindmap]);

  // Configure force simulation
  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0) {
      const graph = graphRef.current;
      
      const linkForce = graph.d3Force('link');
      if (linkForce) {
        linkForce.distance(150);
        linkForce.strength(0.5);
      }
      
      const chargeForce = graph.d3Force('charge');
      if (chargeForce) {
        chargeForce.strength(-400);
      }
      
      const centerForce = graph.d3Force('center');
      if (centerForce) {
        centerForce.strength(0.1);
      }
      
      graph.d3ReheatSimulation();
    }
  }, [graphData.nodes.length, graphData.links.length]);

  const handleNodeClick = (node: GraphNode) => {
    const now = Date.now();
    const lastClick = lastClickRef.current;

    // Check for double-click (within 300ms)
    if (lastClick && lastClick.nodeId === node.id && now - lastClick.timestamp < 300) {
      // Double-click detected
      const nodeData = mindmap?.nodes.find(n => n.id === node.id);
      if (nodeData) {
        setEditingNode(nodeData);
        setShowNodeModal(true);
      }
      lastClickRef.current = null;
      return;
    }

    // Single click
    lastClickRef.current = { nodeId: node.id, timestamp: now };

    if (fromNodeId && node.id !== fromNodeId) {
      handleCreateEdge(node.id);
    } else {
      setSelectedNodeId(node.id);
    }
  };


  const handleNodeDrag = (node: GraphNode) => {
    // Allow dragging, position will be saved on drag end
  };

  const handleNodeDragEnd = async (node: GraphNode) => {
    if (!mindmap || !node.x || !node.y) return;

    try {
      await MindmapService.updateNode(mindmap.id, node.id, {
        x: node.x,
        y: node.y
      });
      await loadMindmap();
    } catch (err) {
      console.error('Error updating node position:', err);
      setError('Failed to save node position');
    }
  };

  const handleLinkClick = (link: GraphLink) => {
    if (window.confirm('Delete this edge?')) {
      handleDeleteEdge(link.id);
    }
  };

  const handleSaveNode = async (label: string, color: string, size: number) => {
    if (!mindmap || !user) return;

    try {
      setError(null);
      if (editingNode) {
        await MindmapService.updateNode(mindmap.id, editingNode.id, {
          label,
          color,
          size
        });
      } else {
        await MindmapService.createNode(mindmap.id, {
          label,
          color,
          size,
          x: newNodePosition?.x,
          y: newNodePosition?.y
        });
      }
      setShowNodeModal(false);
      setEditingNode(null);
      setNewNodePosition(null);
      await loadMindmap();
    } catch (err) {
      console.error('Error saving node:', err);
      setError('Failed to save node');
    }
  };

  const handleDeleteNode = async () => {
    if (!mindmap || !selectedNodeId) return;

    if (!window.confirm('Are you sure you want to delete this node?')) return;

    try {
      setError(null);
      await MindmapService.deleteNode(mindmap.id, selectedNodeId);
      setSelectedNodeId(null);
      await loadMindmap();
    } catch (err) {
      console.error('Error deleting node:', err);
      setError('Failed to delete node');
    }
  };

  const handleCreateEdge = async (toNodeId: number) => {
    if (!mindmap || !fromNodeId) return;

    try {
      setError(null);
      await MindmapService.createEdge(mindmap.id, {
        from_node_id: fromNodeId,
        to_node_id: toNodeId
      });
      setFromNodeId(null);
      await loadMindmap();
    } catch (err) {
      console.error('Error creating edge:', err);
      setError('Failed to create edge');
    }
  };

  const handleDeleteEdge = async (edgeId: number) => {
    if (!mindmap) return;

    try {
      setError(null);
      await MindmapService.deleteEdge(mindmap.id, edgeId);
      await loadMindmap();
    } catch (err) {
      console.error('Error deleting edge:', err);
      setError('Failed to delete edge');
    }
  };

  const handleAddNode = () => {
    setEditingNode(null);
    setNewNodePosition(null);
    setShowNodeModal(true);
  };

  if (!user) {
    return (
      <div className="mindmap-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">/mindmap</h1>}
        />
        <div className="mindmap-empty-state">
          <p>Please sign in to access mindmaps</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mindmap-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">/mindmap</h1>}
        />
        <div className="mindmap-loading">
          <div className="loading-spinner"></div>
          <p>Loading mindmap...</p>
        </div>
      </div>
    );
  }

  if (error && !mindmap) {
    return (
      <div className="mindmap-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">/mindmap</h1>}
        />
        <div className="mindmap-error">
          {error}
          <button onClick={loadMindmap} className="mindmap-retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  if (!mindmap) {
    return null;
  }

  return (
    <div className="mindmap-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">{mindmap.title}</h1>}
        rightActions={
          <div className="mindmap-canvas-actions">
            <button className="mindmap-add-node-btn" onClick={handleAddNode}>
              + Node
            </button>
            {selectedNodeId && (
              <>
                <button 
                  className="mindmap-create-edge-btn" 
                  onClick={() => setFromNodeId(selectedNodeId)}
                  title="Click another node to create edge"
                >
                  Create Edge
                </button>
                <button className="mindmap-delete-node-btn" onClick={handleDeleteNode}>
                  Delete Node
                </button>
              </>
            )}
            {fromNodeId && (
              <button 
                className="mindmap-cancel-edge-btn" 
                onClick={() => setFromNodeId(null)}
              >
                Cancel Edge
              </button>
            )}
            <button className="mindmap-back-btn" onClick={() => navigate('/mindmap')}>
              ← Back
            </button>
          </div>
        }
      />

      {error && (
        <div className="mindmap-error">
          {error}
          <button onClick={() => setError(null)} className="mindmap-close-error-btn">×</button>
        </div>
      )}

      <div className="mindmap-canvas-wrapper">
        {graphData.nodes.length === 0 ? (
          <div className="mindmap-empty-state">
            <p>No nodes yet. Double-click on canvas or click "+ Node" to add your first node.</p>
          </div>
        ) : (
          <>
            <ForceGraph2D
              ref={graphRef}
              graphData={graphData}
              nodeId="id"
              linkDirectionalArrowLength={6}
              linkDirectionalArrowRelPos={1}
              linkDirectionalArrowColor="#666666"
              onNodeClick={handleNodeClick}
              onNodeDrag={handleNodeDrag}
              onNodeDragEnd={handleNodeDragEnd}
              onLinkClick={handleLinkClick}
              cooldownTicks={100}
              // Custom node rendering
              nodeCanvasObject={(node: GraphNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
                if (typeof node.x !== 'number' || typeof node.y !== 'number') return;
                
                const label = node.label;
                const fontSize = Math.max(12 / globalScale, 8);
                const radius = node.size / 2 || 10;
                const isSelected = node.id === selectedNodeId;
                
                // Draw circle
                ctx.beginPath();
                ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
                ctx.fillStyle = node.color || '#ffffff';
                ctx.fill();
                ctx.strokeStyle = isSelected ? '#333333' : '#666666';
                ctx.lineWidth = isSelected ? 3 : 2;
                ctx.stroke();
                
                // Draw label below node
                ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                ctx.fillStyle = '#333333';
                ctx.fillText(label, node.x, node.y + radius + 5);
              }}
              nodePointerAreaPaint={(node: any, color: string, ctx: CanvasRenderingContext2D) => {
                if (typeof node.x !== 'number' || typeof node.y !== 'number') return;

                const pointerAreaRadius = (node.size || 20) / 2 + 5;
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.arc(node.x, node.y, pointerAreaRadius, 0, 2 * Math.PI, false);
                ctx.fill();
              }}
              // Custom link rendering
              linkCanvasObject={(link: GraphLink, ctx: CanvasRenderingContext2D, globalScale: number) => {
                const source = link.source as GraphNode;
                const target = link.target as GraphNode;
                
                if (typeof source.x !== 'number' || typeof source.y !== 'number' || typeof target.x !== 'number' || typeof target.y !== 'number') return;
                
                const nodeRadius = 10;
                const dx = target.x - source.x;
                const dy = target.y - source.y;
                const distance = Math.sqrt(dx * dx + dy * dy);
                const unitX = dx / distance;
                const unitY = dy / distance;
                
                const arrowLength = 8;
                const arrowX = target.x - unitX * nodeRadius;
                const arrowY = target.y - unitY * nodeRadius;
                
                // Draw line
                ctx.beginPath();
                ctx.moveTo(source.x, source.y);
                ctx.lineTo(arrowX, arrowY);
                ctx.strokeStyle = '#e0e0e0';
                ctx.lineWidth = 2;
                ctx.stroke();
                
                // Draw arrow head
                const angle = Math.atan2(dy, dx);
                ctx.beginPath();
                ctx.moveTo(arrowX, arrowY);
                ctx.lineTo(
                  arrowX - arrowLength * Math.cos(angle - Math.PI / 6),
                  arrowY - arrowLength * Math.sin(angle - Math.PI / 6)
                );
                ctx.lineTo(
                  arrowX - arrowLength * Math.cos(angle + Math.PI / 6),
                  arrowY - arrowLength * Math.sin(angle + Math.PI / 6)
                );
                ctx.closePath();
                ctx.fillStyle = '#666666';
                ctx.fill();
                
                // Draw label if exists
                if (link.label) {
                  const midX = (source.x + target.x) / 2;
                  const midY = (source.y + target.y) / 2;
                  const fontSize = Math.max(12 / globalScale, 8);
                  ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';
                  ctx.fillStyle = '#666666';
                  ctx.strokeStyle = '#ffffff';
                  ctx.lineWidth = 4;
                  ctx.strokeText(link.label, midX, midY);
                  ctx.fillText(link.label, midX, midY);
                }
              }}
              enableZoomInteraction={true}
              enablePanInteraction={true}
              minZoom={0.1}
              maxZoom={3}
            />
            
            {/* Zoom controls */}
            <div className="mindmap-graph-zoom-controls">
              <button
                className="mindmap-graph-zoom-btn"
                onClick={() => {
                  if (graphRef.current) {
                    graphRef.current.zoom(graphRef.current.zoom() * 1.2, 400);
                  }
                }}
                title="Zoom In"
              >
                +
              </button>
              <button
                className="mindmap-graph-zoom-btn"
                onClick={() => {
                  if (graphRef.current) {
                    graphRef.current.zoom(graphRef.current.zoom() / 1.2, 400);
                  }
                }}
                title="Zoom Out"
              >
                −
              </button>
              <button
                className="mindmap-graph-zoom-btn"
                onClick={() => {
                  if (graphRef.current) {
                    graphRef.current.zoomToFit(400, 50);
                  }
                }}
                title="Reset Zoom / Fit to Screen"
              >
                ⌂
              </button>
            </div>
          </>
        )}
      </div>

      {showNodeModal && (
        <MindmapNodeModal
          node={editingNode}
          onSave={handleSaveNode}
          onClose={() => {
            setShowNodeModal(false);
            setEditingNode(null);
            setNewNodePosition(null);
          }}
        />
      )}

      {fromNodeId && (
        <div className="mindmap-edge-creation-hint">
          <p>Click on a node to create an edge from the selected node</p>
          <button onClick={() => setFromNodeId(null)}>Cancel</button>
        </div>
      )}
    </div>
  );
};

export default MindmapCanvas;
